import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Form from '@shared/atoms/Form';
import FormInput from '@shared/atoms/FormFields/FormInput';
import Dialog from '@shared/molecules/Dialog';
import theme from '@shared/themes/default';
import { useForm } from '@shared/utils';

import { useAppGlobals } from '../../../contexts/AppContext';

import InvoiceForm from './InvoiceForm';
import { createOrganization } from '../graphql/mutations';
import { CORE_ORGANIZATION_FIELDS } from '../graphql/fragments';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.s};
`;

const CreateOrganizationDialog = ({
  onClose,
  onCreateOrganization,
  open,
  setErrorMessage,
  setHasError,
  setHasSuccess,
  setSuccessMessage,
}) => {
  const { domain, useGraphQLMutation } = useAppGlobals();
  const { t } = useTranslation();

  const [createOrganizationMutation, createOrganizationMutationState] =
    useGraphQLMutation(createOrganization, {
      update(cache, { data: { createOrganization } }) {
        onCreateOrganization({
          ...createOrganization,
          registeredMembers: 0,
        });
        setSuccessMessage(
          t('backoffice.organizations.createOrganization.successfullyCreated', {
            name: createOrganization.name,
          })
        );
        setHasSuccess(true);
        cache.modify({
          fields: {
            listOrganizations(existingOrganizations = []) {
              const newOrganizationRef = cache.writeFragment({
                data: createOrganization,
                fragment: CORE_ORGANIZATION_FIELDS,
              });
              return [...existingOrganizations, newOrganizationRef];
            },
          },
        });
      },
      onError() {
        setErrorMessage(
          t('backoffice.organizations.createOrganization.failedToCreate')
        );
        setHasError(true);
      },
      onCompleted() {
        onClose();
      },
    });

  const validateOrganizationNumber = (value) => {
    if (!value) return true;

    if (domain === 'es') {
      return /^[A-Z0-9][0-9]{7}[A-Z0-9]$/i.test(value);
    }

    return /^[0-9]{9}$/.test(value);
  };

  const {
    disableSubmit,
    errors,
    handleSubmit,
    handleBlur,
    handleChange,
    properties,
    submitting,
    values,
  } = useForm({
    fields: {
      name: {
        required: true,
      },
      type: {
        default: 'ENTERPRISE',
      },
      organizationNumber: {
        maxLength: 9,
        upperCase: true,
        validation: validateOrganizationNumber,
        validationMessage:
          domain === 'no'
            ? t('errors.organizationNumberValidation')
            : t('errors.organizationNifValidation'),
      },
      invoiceName: {},
      invoiceEmail: {
        lowerCase: true,
        type: 'email',
      },
      invoiceAddress: {},
      invoiceZip: {
        maxLength: 4,
      },
      invoiceCity: {},
      invoiceSendVia: {
        default: 'none',
      },
      invoicePeriod: {
        default: 'none',
      },
      invoiceNote: {},
      invoiceReference: {},
      purchasedLicenses: {
        mode: 'numeric',
        pattern: '[0-9]*',
        validation: (value) => /^[0-9]*$/.test(value),
      },
      startDate: {},
    },
    onSubmitHook: (organization) => {
      const input = {};
      Object.entries(organization).forEach(([key, value]) => {
        if (
          (key === 'invoicePeriod' || key === 'invoiceSendVia') &&
          value === 'none'
        ) {
          return;
        }
        if (value) {
          input[key] = value;
        }
      });

      if (input.purchasedLicenses) {
        input.purchasedLicenses = parseInt(input.purchasedLicenses, 10);
      }

      createOrganizationMutation({
        variables: {
          input,
        },
      });
    },
    onSubmitHookEnded: !createOrganizationMutationState.loading,
  });

  const actions = [
    {
      ariaLabel: t('generic.cancel'),
      label: t('generic.cancel'),
      handler: onClose,
      type: 'secondaryOutline',
    },
    {
      ariaLabel: t('backoffice.organizations.createOrganization.create'),
      label: t('backoffice.organizations.createOrganization.create'),
      handler: handleSubmit,
      disabled: disableSubmit,
      submit: true,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      actions={actions}
      title={t('backoffice.organizations.createOrganization.title')}
      ariaLabel={t('backoffice.organizations.createOrganization.title')}
      loading={submitting}
    >
      <Form onSubmit={handleSubmit}>
        <FormWrapper>
          <FormInput
            label={t(
              'backoffice.organizations.createOrganization.organizationName'
            )}
            id="name"
            name="name"
            placeholder={t(
              'backoffice.organizations.createOrganization.organizationNamePlaceholder'
            )}
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            properties={properties}
            values={values}
            theme="light"
          />
          <InvoiceForm
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            properties={properties}
            theme="light"
            values={values}
            showSubscriptionData
          />
        </FormWrapper>
      </Form>
    </Dialog>
  );
};

CreateOrganizationDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreateOrganization: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setHasError: PropTypes.func.isRequired,
  setHasSuccess: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};

export default CreateOrganizationDialog;
